import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon, type IconName } from "./Icon";
import { Logo } from "./Logo";
import { VeniceCreditsBadge } from "./VeniceCreditsBadge";
import { useAuth } from "./AuthContext";

const navigation: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: "/app/catalog", label: "Catalog", icon: "compass" },
  { to: "/app/campaigns", label: "Campaigns", icon: "play" },
  { to: "/app/create", label: "Create", icon: "wand" },
  { to: "/app/libraries", label: "Libraries", icon: "layers" },
];

const secondary: Array<{ to: string; label: string; icon: IconName }> = [
  { to: "/app/profile", label: "Profile", icon: "user" },
  { to: "/app/admin", label: "Admin", icon: "shield" },
];

const pageDetails = [
  ["/app/dialect-coach", "Valley Voice Academy"],
  ["/app/campaign/", "Campaign"], ["/app/catalog", "Discover worlds"], ["/app/campaigns", "Your campaigns"],
  ["/app/create", "Create a world"], ["/app/libraries", "Asset libraries"], ["/app/profile", "Your profile"], ["/app/admin", "Administration"],
] as const;

export function AppShell() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCampaign = location.pathname.startsWith("/app/campaign/");
  const [collapsed, setCollapsed] = useState(isCampaign);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pageTitle = useMemo(() => pageDetails.find(([path]) => location.pathname.startsWith(path))?.[1] ?? "tfGames.ai", [location.pathname]);
  useEffect(() => setCollapsed(isCampaign), [isCampaign]);
  if (!session) return null;

  const closeMobile = () => setMobileOpen(false);
  return (
    <div className={`app-shell ${collapsed ? "app-shell--collapsed" : ""}`}>
      {mobileOpen && <button className="nav-scrim" aria-label="Close navigation" onClick={closeMobile}/>} 
      <aside className={`side-nav ${mobileOpen ? "side-nav--open" : ""}`}>
        <div className="side-nav__head">
          <Logo compact={collapsed}/>
          <button className="icon-button collapse-button" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}>
            <Icon name="chevron" className={collapsed ? "flip-x" : ""} size={17}/>
          </button>
          <button className="icon-button mobile-close" onClick={closeMobile} aria-label="Close navigation">×</button>
        </div>
        <nav className="primary-nav" aria-label="Primary">
          <span className="nav-caption">Explore</span>
          {navigation.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={closeMobile} className={({ isActive }) => `nav-item ${isActive ? "is-active" : ""}`} title={collapsed ? item.label : undefined}>
              <Icon name={item.icon} size={19}/><span>{item.label}</span>{item.label === "Create" && <i>AI</i>}
            </NavLink>
          ))}
          <span className="nav-caption nav-caption--second">Account</span>
          {secondary.filter((item) => item.label !== "Admin" || session.user.role === "admin").map((item) => (
            <NavLink key={item.to} to={item.to} onClick={closeMobile} className={({ isActive }) => `nav-item ${isActive ? "is-active" : ""}`} title={collapsed ? item.label : undefined}>
              <Icon name={item.icon} size={19}/><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="side-nav__bottom">
          <VeniceCreditsBadge compact={collapsed}/>
          <button className="account-card" onClick={() => navigate("/app/profile")}>
            <span className="avatar">{session.user.username.slice(0, 2).toUpperCase()}</span>
            {!collapsed && <span className="account-copy"><strong>{session.user.username}</strong><small>Beta explorer</small></span>}
            {!collapsed && <Icon name="more" size={18}/>} 
          </button>
          {!collapsed && <button className="quiet-button logout-button" onClick={() => void logout().then(() => navigate("/login"))}><Icon name="logout" size={16}/>Sign out</button>}
        </div>
      </aside>
      <div className="app-stage">
        <header className="app-header">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Icon name="menu"/></button>
          <div><span className="eyebrow brand-name">tfGames.ai</span><h1>{pageTitle}</h1></div>
          <div className="header-right">
            <div className="live-pill"><span/>All systems ready</div>
            <VeniceCreditsBadge compact/>
            <button className="avatar avatar--button" onClick={() => navigate("/app/profile")}>{session.user.username.slice(0, 2).toUpperCase()}</button>
          </div>
        </header>
        <main className="app-content"><Outlet/></main>
      </div>
      <nav className="mobile-tabbar" aria-label="Mobile navigation">
        {navigation.slice(0, 4).map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? "is-active" : ""}>
            <Icon name={item.icon} size={20}/><span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
